import { useIntl } from 'react-intl';
import { graphql, useStaticQuery } from 'gatsby';
import { useQuery } from '@tanstack/react-query';
import { Container, Notification } from '@printdeal/ui-library';
import { Markdown } from '@printdeal/markdown/react';
import { useLocation } from '../hooks/useSafeLocation';
import { ServiceFactory } from '../services/ServiceFactory';

interface Message {
  id: string;
  message: string;
}

interface Node {
  node_locale: string;
  childContentfulPortalMessageMessageTextNode?: Message
}

interface QueryResultProps {
  allContentfulPortalMessage: {
    nodes?: Node[];
  }
}

const query = graphql`
{
  allContentfulPortalMessage(filter: {emergencyMessage: {ne: true}}) {
    nodes {
      node_locale
      childContentfulPortalMessageMessageTextNode {
        id
        message
      }
    }
  }
}
`;

const portalMessageApi = ServiceFactory.createPortalMessageService();

const PortalMessages = () => {
  const intl = useIntl();
  const { origin } = useLocation();
  const { allContentfulPortalMessage = { nodes: [] } } = useStaticQuery<QueryResultProps>(query);

  const { data: messagesFromApi } = useQuery({
    queryKey: ['portalMessages'],
    queryFn: () => portalMessageApi.getPortalMessages(intl.locale),
  });

  const staticMessages = (allContentfulPortalMessage.nodes || []).reduce((messages, node) => {
    if (intl.locale === node.node_locale && node.childContentfulPortalMessageMessageTextNode?.message) {
      messages.push(node.childContentfulPortalMessageMessageTextNode);
    }
    return messages;
  }, [] as Message[]);

  if ((messagesFromApi === undefined || messagesFromApi.length === 0) && staticMessages.length === 0) return null;

  return (
    <Notification variant="primary" className="pd:rounded-t-0 pd:text-sm pd:md:text-md">
      <Container>
        {staticMessages.map(({ message, id }) => (
          <Markdown origin={origin} key={id}>{message}</Markdown>
        ))}
        {messagesFromApi?.map((curr) => (
          <p key={curr.message}>{curr.message}</p>
        ))}
      </Container>
    </Notification>
  );
};

export default PortalMessages;
